/**
 * Пакет проверки розыгрыша (формат market-table/verification, версия 1).
 *
 * Выдаётся каждому участнику после расчёта. Содержит всё, что нужно, чтобы самостоятельно
 * проверить розыгрыш и сверить его с любыми внешними источниками:
 *   - правила стола и временную шкалу (когда что было раскрыто);
 *   - криптографическое обязательство (хеш, соль, параметры);
 *   - состав агрегата, базовые цены и исходные минутные бары каждой акции (с UTC-временем
 *     рынка — их можно сравнить с любым поставщиком котировок);
 *   - бары агрегата в той версии, что видели игроки, P₀, P₁, Δ;
 *   - все ходы всех участников, их итоговые прогнозы и (в личном пакете) историю своего прогноза;
 *   - расчёт: банки, веса, выплаты, возвраты, комиссию по кругам;
 *   - все проводки кредитов по розыгрышу.
 * Пакет подписан ключом Ed25519 сервера (подпись по каноническому JSON).
 */
import type { Outcome, TableRules } from '@mt/game-core';
import type { MinuteBar } from '@mt/market-index';
import type { ActionType, Bar, Direction, Minor, SettlementView } from '@mt/protocol';

export const PACKAGE_FORMAT = 'market-table/verification';
export const PACKAGE_VERSION = 1;

export interface ComponentAudit {
  symbol: string;
  name: string;
  /** закрытие акции на баре P₀ — база индекса */
  basePrice: number;
  /** средний объём акции по окну истории — для «активности» */
  avgVolume: number;
  /** исходные минутные бары поставщика (только минуты со сделками), time — начало минуты UTC */
  bars: (MinuteBar & { time: string })[];
}

export interface ServedBar extends Bar {
  /** номер бара на игровой шкале: −1 — бар P₀, 3 — целевая свеча */
  k: number;
  /** рыночная минута (UTC), из которой построен бар */
  marketTime: string;
}

export interface ActionRecord {
  seq: number;
  at: number;
  /** круг 1–3 (0 — подготовка, взносы) */
  round: number;
  kind: 'action' | 'autoFold' | 'uncalledReturn' | 'roundClosed' | 'stage' | 'reveal';
  playerId: string | null;
  name: string | null;
  action?: ActionType | 'ante';
  /** для ставки/райза — итоговая ставка игрока в круге; для колла/олл-ина — доплата */
  amount?: Minor;
  auto?: boolean;
  text: string;
}

export interface ParticipantRecord {
  playerId: string;
  name: string;
  isBot: boolean;
  /** позиция в порядке хода (для детерминированного распределения остатков) */
  order: number;
  ante: Minor;
  roundBets: Minor[];
  folded: boolean;
  direction: Direction | null;
  points: number | null;
  pointsTicks: number | null;
}

export interface ForecastChange {
  at: number;
  direction: Direction | null;
  points: number | null;
}

export interface TransferRecord {
  seq: number;
  at: number;
  from: string;
  to: string;
  amount: Minor;
  kind: string;
  round?: number;
}

export interface VerificationPackage {
  format: typeof PACKAGE_FORMAT;
  version: typeof PACKAGE_VERSION;
  issuedAt: string;
  notice: string;
  howToVerify: string[];
  game: {
    gameId: string;
    roomId: string;
    mode: 'live' | 'replay';
    source: string;
    sourceDescription: string;
  };
  rules: TableRules & { stageMs: number; rounds: number };
  timeline: {
    startAt: number;
    minuteMs: number;
    lockAt: number;
    targetCloseAt: number;
    settledAt: number;
  };
  index: {
    formula: string;
    indexBase: number;
    tick: number;
    sessionId: string;
    universeVersion: string;
    p0MarketTime: string;
    targetMarketTime: string;
    historyBars: number;
    components: ComponentAudit[];
    servedBars: ServedBar[];
  };
  commitment: { hash: string; salt: string; payload: string };
  participants: ParticipantRecord[];
  actions: ActionRecord[];
  outcome: Outcome;
  settlement: SettlementView;
  ledger: TransferRecord[];
  viewer: { playerId: string; name: string; forecastHistory: ForecastChange[] } | null;
}

export interface SignedPackage {
  /** алгоритм и открытый ключ сервера (32 байта, base64) */
  signature: { algorithm: 'Ed25519'; keyId: string; publicKey: string; value: string; signedContent: 'canonical-json(package)' };
  package: VerificationPackage;
}

export type CheckStatus = 'ok' | 'fail' | 'warn' | 'skip';

export interface Check {
  id: string;
  title: string;
  status: CheckStatus;
  details: string[];
}
